import React, { memo } from 'react';
import { UserHeader } from '../UseHeader/UserHeader';
import { OptionsMenu } from '../OptionsMenu/OptionsMenu';
import { CommentText } from '../CommentText/CommentText';
import type { BaseCommentBodyProps } from './BaseCommentBody.types';

/**
 * BaseCommentBody - Reusable component for comment display logic
 *
 * Eliminates duplication between main comments and replies by providing
 * a single implementation of UserHeader + OptionsMenu + CommentText pattern.
 */
const BaseCommentBodyComponent: React.FC<BaseCommentBodyProps> = ({
  // User data
  commentAuthor,
  currentUserEmail,

  // Content data
  text,
  commentMentions,
  createdAt,
  editedAt,
  section,
  isEditing = false,
  isEditSkeleton = false,
  authorEmail,

  // Menu configuration
  menuOptions = [],
  onMenuOptionClick,

  // Edit handlers
  onTextEdit,
  onEditStart,
  onEditComplete,
  onEditCancel,
  // Styling customization
  headerClassName = 'comment-card__header',
  contentClassName = 'comment-card__content',
  inputConfig,
  iconOverrides,
  showEditedAt,
  submitOnBlur,
  attachments,
  communicationMode
}) => {
  return (
    <>
      <div className={headerClassName}>
        <UserHeader
          uid={commentAuthor.uid}
          fullName={commentAuthor.fullName}
          email={commentAuthor.email}
        />
        {menuOptions.length > 0 && onMenuOptionClick && (
          <OptionsMenu
            options={menuOptions}
            onOptionClick={onMenuOptionClick}
            triggerIcon={iconOverrides?.menuTriggerIcon}
            triggerStyleOverrides={iconOverrides?.menuTriggerIconStyles}
          />
        )}
      </div>{' '}
      <div className={contentClassName}>
        <CommentText
          text={text}
          commentMentions={commentMentions}
          createdAt={createdAt}
          editedAt={editedAt}
          section={section}
          isEditing={isEditing}
          isEditSkeleton={isEditSkeleton}
          authorEmail={authorEmail}
          currentUserEmail={currentUserEmail}
          onTextEdit={onTextEdit}
          onEditStart={onEditStart}
          onEditComplete={onEditComplete}
          onEditCancel={onEditCancel}
          inputConfig={inputConfig}
          iconOverrides={iconOverrides}
          showEditedAt={showEditedAt}
          submitOnBlur={submitOnBlur}
          attachments={attachments}
          communicationMode={communicationMode}
        />
      </div>
    </>
  );
};

export const BaseCommentBody = memo(BaseCommentBodyComponent);
